import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bot, Play, Pause, RotateCcw, Trash2, Settings, ExternalLink,
  MessageSquare, Clock, Zap, TrendingUp, AlertTriangle, CheckCircle2,
  XCircle, Search, Filter, ChevronDown, Plus, Activity, BarChart3,
  Users, Globe, MoreVertical, Copy, Eye, Power,
} from "lucide-react";
import { toast } from "sonner";

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const { t } = useTranslation();
  const config = {
    active: { label: t("agents.status_active", "Active"), color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: CheckCircle2 },
    paused: { label: t("agents.status_paused", "Paused"), color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: Pause },
    error: { label: t("agents.status_error", "Error"), color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", icon: XCircle },
    deploying: { label: t("agents.status_deploying", "Deploying"), color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30", icon: Clock },
  }[status] || { label: status, color: "text-zinc-400", bg: "bg-zinc-500/10", border: "border-zinc-500/30", icon: AlertTriangle };

  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] px-2 py-1 ${config.bg} ${config.color} border ${config.border} uppercase tracking-wider`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// ─── Usage Bar ────────────────────────────────────────────────────────────────
function UsageBar({ value, max, color = "amber" }) {
  const pct = Math.min(Math.round((value / max) * 100), 100);
  const colorMap = {
    amber: "bg-amber-500",
    emerald: "bg-emerald-500",
    blue: "bg-blue-500",
    red: "bg-red-500",
  };
  return (
    <div className="h-1.5 bg-zinc-800 w-full">
      <div className={`h-full ${colorMap[color]} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Agent Card ──────────────────────────────────────────────────────────────
function AgentCard({ agent, onPause, onRestart, onDelete, onConfigure, onView }) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="border border-zinc-800 bg-zinc-950/30 hover:border-zinc-700 transition-colors">
      {/* Header */}
      <div className="p-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center text-2xl bg-zinc-950/50">
            {agent.avatar}
          </div>
          <div>
            <h3 className="font-display text-white text-sm">{agent.name}</h3>
            <p className="font-mono text-[10px] text-zinc-500">{agent.type} · {agent.plan}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={agent.status} />
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 border border-zinc-700 hover:border-zinc-600 transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-zinc-400" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 z-10 w-48 border border-zinc-700 bg-zinc-950 shadow-xl">
                <button
                  onClick={() => { onView(agent); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {t("agents.view_details", "View Details")}
                </button>
                <button
                  onClick={() => { onConfigure(agent); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  {t("agents.configure", "Configure")}
                </button>
                {agent.status === "active" && (
                  <button
                    onClick={() => { onPause(agent); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[11px] text-amber-400 hover:bg-zinc-800 transition-colors"
                  >
                    <Pause className="w-3.5 h-3.5" />
                    {t("agents.pause", "Pause")}
                  </button>
                )}
                {agent.status === "paused" && (
                  <button
                    onClick={() => { onRestart(agent); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[11px] text-emerald-400 hover:bg-zinc-800 transition-colors"
                  >
                    <Play className="w-3.5 h-3.5" />
                    {t("agents.resume", "Resume")}
                  </button>
                )}
                {agent.status === "error" && (
                  <button
                    onClick={() => { onRestart(agent); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[11px] text-blue-400 hover:bg-zinc-800 transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    {t("agents.restart", "Restart")}
                  </button>
                )}
                <div className="border-t border-zinc-800" />
                <button
                  onClick={() => { setShowDeleteConfirm(true); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[11px] text-red-400 hover:bg-zinc-800 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t("agents.delete", "Delete")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="text-center">
            <p className="font-display text-lg text-white">{agent.messages.toLocaleString()}</p>
            <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider">
              {t("agents.messages", "Messages")}
            </p>
          </div>
          <div className="text-center">
            <p className="font-display text-lg text-white">{agent.uptime}%</p>
            <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider">
              {t("agents.uptime", "Uptime")}
            </p>
          </div>
          <div className="text-center">
            <p className="font-display text-lg text-white">{agent.creditsUsed}/{agent.creditsTotal}</p>
            <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider">
              {t("agents.credits", "Credits")}
            </p>
          </div>
        </div>
        <UsageBar value={agent.creditsUsed} max={agent.creditsTotal} color={agent.creditsUsed / agent.creditsTotal > 0.8 ? "red" : "amber"} />
      </div>

      {/* Channels */}
      <div className="border-t border-zinc-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {agent.channels.map((ch) => (
              <span
                key={ch}
                className="font-mono text-[9px] px-2 py-0.5 border border-zinc-700 text-zinc-400 uppercase"
              >
                {ch}
              </span>
            ))}
          </div>
          <span className="font-mono text-[9px] text-zinc-600">
            {t("agents.deployed", "Deployed")} {agent.deployedAt}
          </span>
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="border-t border-red-500/30 p-4 bg-red-500/5">
          <p className="font-mono text-[11px] text-red-400 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {t("agents.delete_confirm", "Are you sure you want to delete this agent? This action cannot be undone.")}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onDelete(agent);
                setShowDeleteConfirm(false);
                toast.success(t("agents.deleted", "Agent deleted successfully."));
              }}
              className="px-4 py-2 bg-red-500 text-white font-mono text-xs uppercase tracking-wider hover:bg-red-400 transition-colors"
            >
              {t("agents.delete", "Delete")}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 border border-zinc-700 font-mono text-xs text-zinc-400 uppercase tracking-wider hover:border-zinc-600 transition-colors"
            >
              {t("agents.cancel", "Cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Summary Stats ────────────────────────────────────────────────────────────
function SummaryStats({ agents }) {
  const { t } = useTranslation();
  const stats = useMemo(() => {
    const total = agents.length;
    const active = agents.filter((a) => a.status === "active").length;
    const paused = agents.filter((a) => a.status === "paused").length;
    const errors = agents.filter((a) => a.status === "error").length;
    const totalMessages = agents.reduce((sum, a) => sum + a.messages, 0);
    const avgUptime = total > 0 ? Math.round(agents.reduce((sum, a) => sum + a.uptime, 0) / total) : 0;
    return { total, active, paused, errors, totalMessages, avgUptime };
  }, [agents]);

  const items = [
    { label: t("agents.total_agents", "Total Agents"), value: stats.total, icon: Bot, color: "text-white" },
    { label: t("agents.active", "Active"), value: stats.active, icon: CheckCircle2, color: "text-emerald-400" },
    { label: t("agents.paused", "Paused"), value: stats.paused, icon: Pause, color: "text-amber-400" },
    { label: t("agents.errors", "Errors"), value: stats.errors, icon: XCircle, color: "text-red-400" },
    { label: t("agents.total_messages", "Total Messages"), value: stats.totalMessages.toLocaleString(), icon: MessageSquare, color: "text-blue-400" },
    { label: t("agents.avg_uptime", "Avg Uptime"), value: `${stats.avgUptime}%`, icon: Activity, color: "text-purple-400" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div key={item.label} className="border border-zinc-800 bg-zinc-950/30 p-3">
            <Icon className={`w-4 h-4 ${item.color} mb-2`} />
            <p className="font-display text-xl text-white">{item.value}</p>
            <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider">{item.label}</p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main MyAgentsDashboard Export ────────────────────────────────────────────
export default function MyAgentsDashboard({ onDeployNew }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilter, setShowFilter] = useState(false);

  // Mock data - in production this would come from API
  const [agents, setAgents] = useState([
    {
      id: "agent_1",
      name: "Support Bot",
      type: "Support Agent",
      plan: "Pro",
      avatar: "🤖",
      status: "active",
      messages: 12847,
      uptime: 99.8,
      creditsUsed: 18400,
      creditsTotal: 25000,
      channels: ["Telegram", "Email"],
      deployedAt: "2 days ago",
    },
    {
      id: "agent_2",
      name: "Sales Assistant",
      type: "Sales Agent",
      plan: "Enterprise",
      avatar: "👨‍💼",
      status: "active",
      messages: 8234,
      uptime: 99.5,
      creditsUsed: 42000,
      creditsTotal: 100000,
      channels: ["WhatsApp", "Slack", "Email"],
      deployedAt: "5 days ago",
    },
    {
      id: "agent_3",
      name: "Marketing Bot",
      type: "Marketing Agent",
      plan: "Starter",
      avatar: "🦊",
      status: "paused",
      messages: 3421,
      uptime: 97.2,
      creditsUsed: 4200,
      creditsTotal: 5000,
      channels: ["Telegram"],
      deployedAt: "1 week ago",
    },
    {
      id: "agent_4",
      name: "Ops Manager",
      type: "General Operations",
      plan: "Pro",
      avatar: "⚡",
      status: "error",
      messages: 156,
      uptime: 45.0,
      creditsUsed: 156,
      creditsTotal: 25000,
      channels: ["Webhook"],
      deployedAt: "3 hours ago",
    },
  ]);

  const filteredAgents = useMemo(() => {
    return agents.filter((a) => {
      if (search && !a.name.toLowerCase().includes(search.toLowerCase()) && !a.type.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      return true;
    });
  }, [agents, search, statusFilter]);

  const handlePause = (agent) => {
    setAgents((prev) => prev.map((a) => a.id === agent.id ? { ...a, status: "paused" } : a));
    toast.success(t("agents.paused_success", "Agent paused successfully."));
  };

  const handleRestart = (agent) => {
    setAgents((prev) => prev.map((a) => a.id === agent.id ? { ...a, status: "active" } : a));
    toast.success(t("agents.restarted_success", "Agent restarted successfully."));
  };

  const handleDelete = (agent) => {
    setAgents((prev) => prev.filter((a) => a.id !== agent.id));
  };

  const handleConfigure = (agent) => {
    toast.info(t("agents.configure_info", "Configuration panel coming soon."));
  };

  const handleView = (agent) => {
    toast.info(t("agents.view_info", "Agent detail page coming soon."));
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-white mb-1">
            {t("agents.my_agents", "My Agents")}
          </h1>
          <p className="font-mono text-xs text-zinc-500">
            {t("agents.my_agents_desc", "Manage your deployed AI agents, monitor performance, and configure settings.")}
          </p>
        </div>
        <button
          onClick={onDeployNew}
          className="flex items-center gap-2 px-5 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t("agents.deploy_new", "Deploy New Agent")}
        </button>
      </div>

      {/* Summary Stats */}
      <SummaryStats agents={agents} />

      {/* Search & Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("agents.search_placeholder", "Search agents...")}
            className="w-full bg-transparent border border-zinc-700 pl-10 pr-4 py-2.5 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
          />
        </div>
        <div className="relative">
          <button
            onClick={() => setShowFilter(!showFilter)}
            className="flex items-center gap-2 px-4 py-2.5 border border-zinc-700 font-mono text-xs text-zinc-400 uppercase tracking-wider hover:border-zinc-600 transition-colors"
          >
            <Filter className="w-3.5 h-3.5" />
            {statusFilter === "all" ? t("agents.all_status", "All Status") : statusFilter}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {showFilter && (
            <div className="absolute right-0 top-10 z-10 w-40 border border-zinc-700 bg-zinc-950 shadow-xl">
              {["all", "active", "paused", "error", "deploying"].map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setShowFilter(false); }}
                  className={`w-full text-left px-3 py-2 font-mono text-[11px] hover:bg-zinc-800 transition-colors ${
                    statusFilter === s ? "text-amber-400" : "text-zinc-400"
                  }`}
                >
                  {s === "all" ? t("agents.all_status", "All Status") : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Agent List */}
      {filteredAgents.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredAgents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onPause={handlePause}
              onRestart={handleRestart}
              onDelete={handleDelete}
              onConfigure={handleConfigure}
              onView={handleView}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 border border-zinc-800 bg-zinc-950/30">
          <Bot className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h3 className="font-display text-lg text-zinc-400 mb-2">
            {agents.length === 0 ? t("agents.no_agents", "No Agents Yet") : t("agents.no_results", "No Results")}
          </h3>
          <p className="font-mono text-xs text-zinc-500 mb-6">
            {agents.length === 0
              ? t("agents.no_agents_desc", "Deploy your first AI agent to get started.")
              : t("agents.no_results_desc", "Try adjusting your search or filter.")}
          </p>
          {agents.length === 0 && onDeployNew && (
            <button
              onClick={onDeployNew}
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-sm font-bold uppercase tracking-wider hover:bg-amber-400 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t("agents.deploy_first", "Deploy Your First Agent")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
